import type { MyTask, MyTaskAssignmentReason } from './useMyTasks';

export const ORG_GROUP_KEY = 'org';
export const ORG_GROUP_LABEL = 'Organization';

/** Where a task lives, which is how the table is grouped. */
export type MyTaskGroup = {
  key: string;
  label: string;
  /** The route a row in this group opens. */
  href: string;
  tasks: MyTask[];
};

/**
 * How a task came to be mine, in words. A task held by a seat or a team is
 * shared work, and a page called My Tasks would otherwise imply it is not.
 */
const REASON_LABEL: Record<MyTaskAssignmentReason, string> = {
  poc: 'Assigned to you',
  user: 'Assigned to you',
  position: 'Assigned to your position',
  team: 'Assigned to your team',
};

export function assignmentLabel(task: MyTask): string {
  return REASON_LABEL[task.assigned_via] ?? 'Assigned to you';
}

/**
 * Where clicking a row goes.
 *
 * Incident tasks have a page of their own. Org tasks do not, so they open the
 * Hub tab that lists them — the same tab the assignment notification links to.
 */
export function taskHref(task: MyTask): string {
  return task.incident_id
    ? `/iap/${task.incident_id}/actions/${task.action_id}`
    : `/hub?openTab=Actions`;
}

export function isOverdue(task: MyTask, now = new Date()): boolean {
  if (task.status === 'Closed' || !task.target_date) return false;
  const target = Date.parse(task.target_date);
  return !Number.isNaN(target) && target < now.getTime();
}

/** Tasks assigned on one incident's action tracker — not org or other incidents. */
export function tasksForIncident(
  tasks: MyTask[],
  incidentId: string
): MyTask[] {
  return tasks.filter((task) => task.incident_id === incidentId);
}

/** Closed vs remaining, for the incident My Tasks header. */
export function closedProgress(tasks: MyTask[]): {
  closed: number;
  total: number;
} {
  return {
    closed: tasks.filter((task) => task.status === 'Closed').length,
    total: tasks.length,
  };
}

export type AssignedProgress = {
  total: number;
  complete: number;
  incomplete: number;
  overdue: number;
  completePct: number;
  incompletePct: number;
  overduePct: number;
};

function percentOf(count: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((count / total) * 100);
}

/**
 * Assigned-to-you complete / incomplete / overdue. Overdue is a subset of
 * incomplete, not a third partition; percents are of the assigned total.
 */
export function assignedProgress(
  tasks: MyTask[],
  now = new Date()
): AssignedProgress {
  const total = tasks.length;
  const complete = tasks.filter((task) => task.status === 'Closed').length;
  const incomplete = total - complete;
  const overdue = tasks.filter((task) => isOverdue(task, now)).length;
  return {
    total,
    complete,
    incomplete,
    overdue,
    completePct: percentOf(complete, total),
    incompletePct: percentOf(incomplete, total),
    overduePct: percentOf(overdue, total),
  };
}

/**
 * Matches on everything a row shows, so pasting an incident name or typing
 * "blocked" narrows to what you would expect.
 */
export function filterMyTasks(tasks: MyTask[], search: string): MyTask[] {
  const term = search.trim().toLowerCase();
  if (!term) return tasks;

  return tasks.filter((task) =>
    [
      task.name,
      task.description ?? '',
      task.status,
      task.priority,
      task.incident_name ?? ORG_GROUP_LABEL,
      assignmentLabel(task),
      task.blocked_by.length ? 'blocked' : '',
    ]
      .join(' ')
      .toLowerCase()
      .includes(term)
  );
}

/**
 * Groups by incident, with the organization last.
 *
 * Org tasks go last rather than first because incident work is what a shift is
 * actually being judged on; standing org tasks are the backlog underneath it.
 */
export function groupMyTasks(tasks: MyTask[]): MyTaskGroup[] {
  const byIncident = new Map<string, MyTaskGroup>();
  const orgTasks: MyTask[] = [];

  tasks.forEach((task) => {
    if (!task.incident_id) {
      orgTasks.push(task);
      return;
    }
    const existing = byIncident.get(task.incident_id);
    if (existing) {
      existing.tasks.push(task);
      return;
    }
    byIncident.set(task.incident_id, {
      key: task.incident_id,
      label: task.incident_name || 'Incident',
      href: `/iap/${task.incident_id}/actions`,
      tasks: [task],
    });
  });

  const groups = [...byIncident.values()].sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  if (orgTasks.length > 0) {
    groups.push({
      key: ORG_GROUP_KEY,
      label: ORG_GROUP_LABEL,
      href: '/hub?openTab=Actions',
      tasks: orgTasks,
    });
  }

  return groups;
}
